import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { TopSupplierBySpend } from "../../../types/contractDashboard";
import styles from "./spendWithTop10Suppliers.module.scss";

interface SpendWithTop10SuppliersProps {
  data: TopSupplierBySpend[];
}

const SpendWithTop10Suppliers: React.FC<SpendWithTop10SuppliersProps> = ({
  data,
}) => {
  const { chartData, total } = useMemo(() => {
    const topSuppliers = data.slice(0, 10).map((supplier) => ({
      name: supplier.supplier_name,
      spend: parseFloat((supplier.total_amount_spent / 1_000_000).toFixed(2)), // Convert to millions
      displayValue: `$${(supplier.total_amount_spent / 1_000_000).toFixed(1)}M`,
    }));

    const totalSpend = topSuppliers.reduce((sum, s) => sum + s.spend, 0);

    return {
      chartData: topSuppliers,
      total: totalSpend,
    };
  }, [data]);

  const BAR_COLOR = "#08ADC2";

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "10px 14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#111827",
              fontWeight: 600,
              mb: 0.5,
              fontSize: "13px",
            }}
          >
            {payload[0].payload.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#08ADC2", fontWeight: 600, fontSize: "14px" }}
          >
            ${payload[0].value.toFixed(2)}M
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#6b7280", fontSize: "11px" }}
          >
            {((payload[0].value / total) * 100).toFixed(1)}% of total spend
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Spend with Top 10 Suppliers
        </Typography>
        <Box className={styles.totalContainer}>
          <Typography variant="h5" className={styles.total}>
            ${total.toFixed(2)}M
          </Typography>
        </Box>
      </Box>

      <Box className={styles.chartContainer} sx={{ position: "relative" }}>
        <ResponsiveContainer width="100%" height={380}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
            barCategoryGap="12%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={{ stroke: "#d1d5db" }}
              tickLine={false}
              tickFormatter={(value) => `$${value}M`}
              domain={[0, "dataMax + 5"]}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: "#374151", fontSize: 11, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              width={120}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.03)" }}
              wrapperStyle={{ zIndex: 1000 }}
            />
            <Bar dataKey="spend" radius={[0, 6, 6, 0]} barSize={26}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={BAR_COLOR} opacity={0.9} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default SpendWithTop10Suppliers;
